import { Routes } from '@angular/router';
import {
  authGuard,
  guestGuard,
  emailVerifiedGuard,
  blumeAdminGuard,
  profileGuard,
  noProfileGuard,
} from './guards';
import { BlumeAdminComponent } from './pages/blume-admin/blume-admin.component';
import { PartnersState } from 'src/app/rxjs-store/blume-admin/partners/partners.state';
import { AddPartnerState } from 'src/app/rxjs-store/blume-admin/add-partner/add-partner.state';
import { PartnerDetailState } from 'src/app/rxjs-store/blume-admin/partner-detail/partner-detail.state';
import { AccountsState } from 'src/app/rxjs-store/blume-admin/accounts/accounts.state';
import { BlumeAdminActivityState } from 'src/app/rxjs-store/blume-admin/activity/blume-admin-activity.state';
import { AdminSkillsState } from 'src/app/rxjs-store/blume-admin/skills/skills.state';
import { provideStates } from '@ngxs/store';
import { WorkspaceComponent } from './pages-old/workspace/workspace.component';
import { WorkspaceUIState } from './pages-old/workspace/store';
import { BlumeAdminUIState } from './pages/blume-admin/store';
import { AddObservationState } from 'src/app/rxjs-store/forms/add-observation';
import { ObservationsState } from 'src/app/rxjs-store/admin/observations';
import { GoalsState } from 'src/app/rxjs-store/admin/goals';
import { emailNotVerifiedGuard } from './guards/email-verified.guard';

export const routes: Routes = [
  // Auth Routes
  {
    path: 'sign-in',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages-old/auth/sign-in/sign-in.page').then((m) => m.SignInPage),
  },
  {
    path: 'sign-up',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages-old/auth/sign-up/sign-up.page').then((m) => m.SignUpPage),
  },
  {
    path: 'auth-actions',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages-old/auth/auth-actions/auth-actions.page').then(
        (m) => m.AuthActionsPage
      ),
  },
  {
    path: 'forgot-password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages-old/auth/forgot-password/forgot-password.page').then(
        (m) => m.ForgotPasswordPage
      ),
  },
  {
    path: 'setup-profile',
    canActivate: [authGuard],
    loadComponent: () => import('./community/setup-profile/setup-profile.page').then(m => m.SetupProfilePage),
  },
  {
    path: 'loading',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages-old/user-initialization/user-initialization.page').then(
        (m) => m.UserInitializationPage
      ),
  },
  {
    path: 'sign-out',
    loadComponent: () =>
      import('./pages-old/sign-out/sign-out.page').then((m) => m.SignOutPage),
  },
  // Account Routes
  {
    path: 'account/verify-email',
    pathMatch: 'full',
    canActivate: [authGuard, emailNotVerifiedGuard],
    loadComponent: () =>
      import('./pages-old/account/verify-email/verify-email.page').then(
        (m) => m.VerifyEmailPage
      ),
  },
  {
    path: 'account/enter-verification-code',
    pathMatch: 'full',
    canActivate: [authGuard, emailNotVerifiedGuard],
    loadComponent: () =>
      import(
        './pages-old/account/enter-verification-code/enter-verification-code.page'
      ).then((m) => m.EnterVerificationCodePage),
  },
  {
    path: 'account/edit',
    pathMatch: 'full',
    canActivate: [authGuard, emailVerifiedGuard],
    loadComponent: () =>
      import('./pages-old/account/account-edit/account-edit.page').then(
        (m) => m.AccountEditPage
      ),
  },
  {
    path: 'account/activity',
    pathMatch: 'full',
    canActivate: [authGuard, emailVerifiedGuard],
    loadComponent: () =>
      import('./pages-old/account/account-activity/account-activity.page').then(
        (m) => m.AccountActivityPage
      ),
  },
  // {
  //   path: 'account',
  //   pathMatch: 'full',
  //   canActivate: [authGuard, emailVerifiedGuard],
  //   loadComponent: () =>
  //     import('./pages-old/account/account-detail/account-detail.page').then(
  //       (m) => m.AccountDetailPage
  //     ),
  // },
  {
    path: 'blume-admin',
    canActivate: [authGuard, emailVerifiedGuard, blumeAdminGuard],
    providers: [
      provideStates([
        PartnersState,
        AddPartnerState,
        PartnerDetailState,
        AccountsState,
        BlumeAdminActivityState,
        BlumeAdminUIState,
        AdminSkillsState,
      ]),
    ],
    component: BlumeAdminComponent,
    children: [
      {
        path: 'home',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-home/blume-admin-home.page'
          ).then((m) => m.BlumeAdminHomePage),
      },
      {
        path: 'accounts/:accountId',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-accounts/blume-admin-accounts-details/blume-admin-accounts-details.page'
          ).then((m) => m.BlumeAdminAccountsDetailsPage),
      },
      {
        path: 'accounts',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-accounts/blume-admin-accounts-list/blume-admin-accounts-list.page'
          ).then((m) => m.BlumeAdminAccountsListPage),
      },
      {
        path: 'partners/add',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-partners/blume-admin-partners-add/blume-admin-partners-add.page'
          ).then((m) => m.BlumeAdminPartnersAddPage),
      },
      {
        path: 'partners/:partnerId/edit',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-partners/blume-admin-partners-edit/blume-admin-partners-edit.page'
          ).then((m) => m.BlumeAdminPartnersEditPage),
      },
      {
        path: 'partners/:partnerId/info',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-partners/blume-admin-partners-detail/blume-admin-partners-detail-info/blume-admin-partners-detail-info.page'
          ).then((m) => m.BlumeAdminPartnersDetailInfoPage),
      },
      {
        path: 'partners/:partnerId/licenses',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-partners/blume-admin-partners-detail/blume-admin-partners-detail-licenses/blume-admin-partners-detail-licenses.page'
          ).then((m) => m.BlumeAdminPartnersDetailLicensesPage),
      },
      {
        path: 'partners/:partnerId/users',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-partners/blume-admin-partners-detail/blume-admin-partners-detail-users/blume-admin-partners-detail-users.page'
          ).then((m) => m.BlumeAdminPartnersDetailUsersPage),
      },
      {
        path: 'partners/:partnerId',
        redirectTo: 'partners/:partnerId/info',
        pathMatch: 'full',
      },
      {
        path: 'partners',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-partners/blume-admin-partners-list/blume-admin-partners-list.page'
          ).then((m) => m.BlumeAdminPartnersListPage),
      },
      {
        path: 'activity',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-activity/blume-admin-activity.page'
          ).then((m) => m.BlumeAdminActivityPage),
      },
      {
        path: 'staff',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-staff/blume-admin-staff.page'
          ).then((m) => m.BlumeAdminStaffPage),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-profile/blume-admin-profile.page'
          ).then((m) => m.BlumeAdminProfilePage),
      },
      {
        path: 'skills/add',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-skills/blume-admin-skills-add/blume-admin-skills-add.page'
          ).then((m) => m.BlumeAdminSkillsAddPage),
      },
      {
        path: 'skills/:skillId/edit',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-skills/blume-admin-skills-edit/blume-admin-skills-edit.page'
          ).then((m) => m.BlumeAdminSkillsEditPage),
      },
      {
        path: 'skills/:skillId/details',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-skills/blume-admin-skills-details/blume-admin-skills-details.page'
          ).then((m) => m.BlumeAdminSkillsDetailsPage),
      },
      {
        path: 'skills',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-skills/blume-admin-skills-list/blume-admin-skills-list.page'
          ).then((m) => m.BlumeAdminSkillsListPage),
      },
      {
        path: 'academy',
        redirectTo: 'academy/modules',
        pathMatch: 'full',
      },
      // Modules routes
      {
        path: 'academy/modules/add',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-academy/academy-module/blume-admin-academy-module-add/blume-admin-academy-module-add.page'
          ).then((m) => m.BlumeAdminAcademyModuleAddPage),
      },
      {
        path: 'academy/modules/:moduleId/edit',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-academy/academy-module/blume-admin-academy-module-edit/blume-admin-academy-module-edit.page'
          ).then((m) => m.BlumeAdminAcademyModuleEditPage),
      },
      {
        path: 'academy/modules/:moduleId/preview',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-academy/academy-module/blume-admin-academy-module-preview/blume-admin-academy-module-preview.page'
          ).then((m) => m.BlumeAdminAcademyModulePreviewPage),
      },
      {
        path: 'academy/modules/:moduleId/detail',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-academy/academy-module/blume-admin-academy-module-detail/blume-admin-academy-module-detail.page'
          ).then((m) => m.BlumeAdminAcademyModuleDetailPage),
      },
      {
        path: 'academy/modules',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-academy/academy-module/blume-admin-academy-module-list/blume-admin-academy-module-list.page'
          ).then((m) => m.BlumeAdminAcademyModuleListPage),
      },
      // Workshops routes
      {
        path: 'academy/workshops',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-academy/workshops/blume-admin-workshops-list/blume-admin-workshops-list.page'
          ).then((m) => m.BlumeAdminWorkshopsListPage),
      },
      // Portfolio routes
      {
        path: 'academy/portfolio',
        loadComponent: () =>
          import(
            './pages/blume-admin/blume-admin-academy/portfolio/blume-admin-portfolio.page'
          ).then((m) => m.BlumeAdminPortfolioPage),
      },
      {
        path: '**',
        redirectTo: 'home',
      },
    ],
  },
  {
    path: 'blume-development',
    canActivate: [authGuard, emailVerifiedGuard, blumeAdminGuard],
    loadComponent: () => import('./apps/blume-development/blume-development.page').then(m => m.BlumeDevelopmentPage),
    children: [
      {
        path: 'home',
        loadComponent: () =>
          import('./apps/blume-development/home/blume-development-home.page').then(m => m.BlumeDevelopmentHomePage),
      },
      {
        path: '**',
        redirectTo: 'home',
      },
    ],
  },
  {
    path: 'workspace/observations/ai-companion',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () =>
      import(
        './pages-old/workspace/observations/observations-ai-companion/observations-ai-companion.page'
      ).then((m) => m.ObservationsAiCompanionPage),
  },
  {
    path: 'workspace/observations/add/:studentId/:skillId',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    providers: [provideStates([AddObservationState])],
    loadComponent: () =>
      import(
        './pages-old/workspace/observations/observations-add/observations-add.page'
      ).then((m) => m.ObservationsAddPage),
  },
  {
    path: 'workspace/goals/:studentId/add',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () =>
      import('./pages-old/workspace/goals/goals-add/goals-add.page').then(
        (m) => m.GoalsAddPage
      ),
  },
  {
    path: 'workspace',
    component: WorkspaceComponent,
    canActivate: [authGuard, emailVerifiedGuard],
    providers: [provideStates([WorkspaceUIState])],
    children: [
      {
        path: 'home',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/home/home.page').then((m) => m.HomePage),
      },
      {
        path: 'development',
        canActivate: [profileGuard],
        loadComponent: () => import('./pages-old/workspace/development/development.page').then(m => m.DevelopmentPage)
      },
      {
        path: 'caseload',
        canActivate: [profileGuard],
        loadComponent: () =>
          import(
            './pages-old/workspace/caseload/caseload.page'
          ).then((m) => m.CaseloadPage),
      },
      {
        path: 'users/add',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/users/users-add/users-add.page').then(
            (m) => m.UsersAddPage
          ),
      },
      {
        path: 'users/:userEmail/edit',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/users/users-edit/users-edit.page').then(
            (m) => m.UsersEditPage
          ),
      },
      {
        path: 'users/:userEmail/profile',
        canActivate: [profileGuard],
        loadComponent: () =>
          import(
            './pages-old/workspace/users/users-profile/users-profile.page'
          ).then((m) => m.UsersProfilePage),
      },
      {
        path: 'users',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/users/users-list/users-list.page').then(
            (m) => m.UsersListPage
          ),
      },
      {
        path: 'students/add',
        canActivate: [profileGuard],
        loadComponent: () =>
          import(
            './pages-old/workspace/students/students-add/students-add.page'
          ).then((m) => m.StudentsAddPage),
      },
      {
        path: 'students/:studentId/edit',
        canActivate: [profileGuard],
        loadComponent: () =>
          import(
            './pages-old/workspace/students/students-edit/students-edit.page'
          ).then((m) => m.StudentsEditPage),
      },
      {
        path: 'students/:studentId/profile',
        canActivate: [profileGuard],
        loadComponent: () =>
          import(
            './pages-old/workspace/students/students-profile/students-profile.page'
          ).then((m) => m.StudentsProfilePage),
      },
      {
        path: 'students',
        canActivate: [profileGuard],
        loadComponent: () =>
          import(
            './pages-old/workspace/students/students-list/students-list.page'
          ).then((m) => m.StudentsListPage),
      },
      {
        path: 'academy',
        redirectTo: 'academy/courses',
        pathMatch: 'full',
      },
      {
        path: 'academy/courses',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/academy/academy/courses/courses-list.page').then(
            (m) => m.CoursesListPage
          ),
      },
      {
        path: 'academy/courses/:courseId/:sectionId',
        canActivate: [profileGuard],
        loadComponent: () =>
          import(
            './pages-old/workspace/academy/pages/course-section/course-section.page'
          ).then((m) => m.CourseSectionPage),
      },
      {
        path: 'academy/courses/assessments/:courseId/:sectionId',
        canActivate: [profileGuard],
        loadComponent: () =>
          import(
            './pages-old/workspace/academy/pages/assessment-detail/assessment-detail.page'
          ).then((m) => m.AssessmentDetailPage),
      },
      {
        path: 'academy/workshops',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/academy/academy/workshops/workshops.page').then(
            (m) => m.WorkshopsPage
          ),
      },
      // TODO: sandbox pages not yet migrated
      // {
      //   path: 'academy/workshops/:workshopId',
      //   canActivate: [profileGuard],
      //   loadComponent: () =>
      //     import('./pages/workspace/sandbox/workshop-detail/workshop-detail.page').then(
      //       (m) => m.WorkshopDetailPage
      //     ),
      // },
      // {
      //   path: 'academy/workshops/:workshopId/complete',
      //   canActivate: [profileGuard],
      //   loadComponent: () =>
      //     import('./pages/workspace/sandbox/workshop-completion/workshop-completion.page').then(
      //       (m) => m.WorkshopCompletionPage
      //     ),
      // },
      {
        path: 'academy/workshops/:workshopId/summary',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/academy/academy/workshops/workshop-summary/workshop-summary.page').then(
            (m) => m.WorkshopSummaryPage
          ),
      },
      {
        path: 'academy/portfolio',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/academy/academy/portfolio/portfolio.page').then(
            (m) => m.PortfolioPage
          ),
      },
      {
        path: 'academy/portfolio/:id',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/academy/academy/portfolio/portfolio-detail/portfolio-detail.page').then(
            (m) => m.PortfolioDetailPage
          ),
      },
      {
        path: 'academy-module/add',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/academy-module/academy-module-add/academy-module-add.page').then(
            (m) => m.AcademyModuleAddPage
          ),
      },
      {
        path: 'academy-module/:moduleId/edit',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/academy-module/academy-module-edit/academy-module-edit.page').then(
            (m) => m.AcademyModuleEditPage
          ),
      },
      {
        path: 'academy-module/:moduleId/detail',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/academy-module/academy-module-detail/academy-module-detail.page').then(
            (m) => m.AcademyModuleDetailPage
          ),
      },
      {
        path: 'workshops/add',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/workshops/workshops-add/workshops-add.page').then(
            (m) => m.WorkshopsAddPage
          ),
      },
      {
        path: 'workshops/:workshopId/edit',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/workshops/workshops-edit/workshops-edit.page').then(
            (m) => m.WorkshopsEditPage
          ),
      },
      {
        path: 'workshops/:workshopId/detail',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/workshops/workshops-detail/workshops-detail.page').then(
            (m) => m.WorkshopsDetailPage
          ),
      },
      {
        path: 'activity',
        canActivate: [profileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/activity/activity.page').then(
            (m) => m.ActivityPage
          ),
      },
      // {
      //   path: 'sandbox',
      //   canActivate: [profileGuard],
      //   loadComponent: () =>
      //     import('./pages/workspace/sandbox/sandbox.page').then(
      //       (m) => m.SandboxPage
      //     ),
      // },
      // {
      //   path: 'sandbox/:workshopId',
      //   canActivate: [profileGuard],
      //   loadComponent: () =>
      //     import('./pages/workspace/sandbox/workshop-detail/workshop-detail.page').then(
      //       (m) => m.WorkshopDetailPage
      //     ),
      // },
      {
        path: 'observations',
        canActivate: [profileGuard],
        providers: [provideStates([ObservationsState])],
        loadComponent: () =>
          import(
            './pages-old/workspace/observations/observations-list/observations-list.page'
          ).then((m) => m.ObservationsListPage),
      },
      {
        path: 'goals/:goalId',
        loadComponent: () =>
          import('./pages-old/workspace/goals/goals-detail/goals-detail.page').then(
            (m) => m.GoalsDetailPage
          ),
      },
      {
        path: 'goals-edit',
        loadComponent: () =>
          import('./pages-old/workspace/goals/goals-edit/goals-edit.page').then(
            (m) => m.GoalsEditPage
          ),
      },
      {
        path: 'goals',
        canActivate: [profileGuard],
        providers: [provideStates([GoalsState])],
        loadComponent: () =>
          import('./pages-old/workspace/goals/goals-list/goals-list.page').then(
            (m) => m.GoalsListPage
          ),
      },
      {
        path: 'objectives',
        canActivate: [authGuard, emailVerifiedGuard, profileGuard],
        loadComponent: () =>
          import(
            './pages-old/workspace/objectives/objectives-list/objectives-list.page'
          ).then((m) => m.ObjectivesListPage),
      },
      {
        path: 'no-profile',
        canActivate: [noProfileGuard],
        loadComponent: () =>
          import('./pages-old/workspace/no-profile/no-profile.page').then(
            (m) => m.NoProfilePage
          ),
      },
      {
        path: '**',
        redirectTo: 'home',
      },
    ],
  },
  {
    path: 'blume-admin-accounts-list',
    loadComponent: () =>
      import(
        './pages/blume-admin/blume-admin-accounts/blume-admin-accounts-list/blume-admin-accounts-list.page'
      ).then((m) => m.BlumeAdminAccountsListPage),
  },
  {
    path: 'blume-admin-accounts-details',
    loadComponent: () =>
      import(
        './pages/blume-admin/blume-admin-accounts/blume-admin-accounts-details/blume-admin-accounts-details.page'
      ).then((m) => m.BlumeAdminAccountsDetailsPage),
  },

  // ADMIN ROUTES
  {
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    path: 'admin/students/add',
    loadComponent: () => import('./pages/admin/students/students-add/students-add.page').then(m => m.StudentsAddPage)
  },
  {
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    path: 'admin/students/:studentId/edit',
    loadComponent: () => import('./pages/admin/students/students-edit/students-edit.page').then(m => m.StudentsEditPage)
  },
  {
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    path: 'admin/users/add',
    loadComponent: () => import('./pages/admin/users/users-add/users-add.page').then(m => m.UsersAddPage)
  },
  {
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    path: 'admin/users/:userId/edit',
    loadComponent: () => import('./pages/admin/users/users-edit/users-edit.page').then(m => m.UsersEditPage)
  },
  {
    path: 'admin',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/admin/admin.page').then(m => m.AdminPage),
    children: [
      {
        path: 'home',
        loadComponent: () => import('./pages/admin/admin-home/admin-home.page').then(m => m.AdminHomePage)
      },
      {
        path: 'students',
        loadComponent: () => import('./pages/admin/students/students-list/students-list.page').then(m => m.StudentsListPage)
      },
      {
        path: 'users',
        loadComponent: () => import('./pages/admin/users/users-list/users-list.page').then(m => m.UsersListPage)
      },
      {
        path: 'goals',
        loadComponent: () => import('./pages/admin/admin-goals/admin-goals.page').then(m => m.AdminGoalsPage)
      },
      {
        path: 'observations',
        loadComponent: () => import('./pages/admin/admin-observations/admin-observations.page').then(m => m.AdminObservationsPage)
      },
      {
        path: 'workshops',
        loadComponent: () => import('./pages/admin/academy/workshops/workshops-list/workshops-list.page').then(m => m.WorkshopsListPage)
      },
      {
        path: 'workshops/add',
        loadComponent: () => import('./pages/admin/academy/workshops/workshops-add/workshops-add.page').then(m => m.WorkshopsAddPage)
      },
      {
        path: 'workshops/:workshopId/edit',
        loadComponent: () => import('./pages/admin/academy/workshops/workshops-edit/workshops-edit.page').then(m => m.WorkshopsEditPage)
      },
      {
        path: 'workshops/:workshopId/summary',
        loadComponent: () => import('./pages/admin/academy/workshops/workshops-list/workshop-summary/workshop-summary.page').then(m => m.WorkshopSummaryPage)
      },
      {
        path: '**',
        redirectTo: 'home',
      },
    ],
  },

  // ACADEMY ROUTES
  {
    path: 'academy',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/academy/academy.page').then(m => m.AcademyPage),
    children: [
      {
        path: 'workshops',
        loadComponent: () => import('./pages/academy/workshops/workshops-list/workshops-list.page').then(m => m.AcademyWorkshopsListPage)
      },
      {
        path: 'workshops/:workshopId',
        loadComponent: () => import('./pages/academy/workshops/workshop-session/workshop-session.page').then(m => m.WorkshopSessionPage)
      },
      {
        path: 'courses',
        loadComponent: () => import('./pages/academy/courses/courses-list.page').then(m => m.CoursesListPage)
      },
      {
        path: 'courses/:courseId/:sectionId',
        loadComponent: () => import('./pages/academy/pages/course-section/course-section.page').then(m => m.CourseSectionPage)
      },
      {
        path: 'courses/:courseId/:sectionId/assessment',
        loadComponent: () => import('./pages/academy/pages/assessment-detail/assessment-detail.page').then(m => m.AssessmentDetailPage)
      },
      {
        path: 'portfolio',
        loadComponent: () => import('./pages/academy/portfolio/portfolio-list.page').then(m => m.PortfolioListPage)
      },
      {
        path: 'portfolio/:portfolioId',
        loadComponent: () => import('./pages/academy/portfolio/portfolio-detail/portfolio-detail.page').then(m => m.PortfolioDetailPage)
      },
      {
        path: '**',
        redirectTo: 'workshops',
      },
    ],
  },

  // Community
  {
    path: 'home',
    canActivate: [authGuard, emailVerifiedGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./apps/community/home/home.page').then(m => m.HomePage)
      },
      {
        path: '**',
        redirectTo: '',
      }
    ]
  },
  // Notebook Routes
  {
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    path: 'notebook/students/:studentId/edit',
    loadComponent: () => import('./pages/notebook/students/students-edit/students-edit.page').then(m => m.StudentsEditPage)
  },
  {
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    path: 'notebook/students/add',
    loadComponent: () => import('./pages/notebook/students/students-add/students-add.page').then(m => m.StudentsAddPage)
  },
  {
    path: 'notebook/students/:studentId/goals/add',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () =>
      import('./pages/notebook/students/goal-add/goal-add.page').then(m => m.GoalAddPage)
  },
  {
    path: 'notebook/home/goals/create/iep-goal',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/notebook/goals/iep-goals/iep-goals-create/iep-goals-create.page').then(m => m.IepGoalsCreatePage)
  },
  // Observation — query params: studentId, skillId, measurementTypes (all optional)
  {
    path: 'notebook/home/collect/record/:studentId/:skillId',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/notebook/collect/observation/observation.page').then(m => m.ObservationPage)
  },
  {
    path: 'notebook/home/collect/record/:observationId/',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/notebook/observations/observations-list/observations-list.page').then(m => m.ObservationsListPage)
  },
  {
    path: 'notebook/home/collect/record',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/notebook/collect/observation/observation.page').then(m => m.ObservationPage)
  },
  {
    path: 'notebook/observations/:observationId',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/notebook/collect/observation/observation.page').then(m => m.ObservationPage)
  },
  {
    path: 'notebook/home/collect/observation/:studentId/:skillId/:measurementTypes',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/notebook/collect/manual-entry/manual-entry-form/manual-entry-form.page').then(m => m.ManualEntryFormPage)
  },
  {
    path: 'notebook/home/collect/observation/:studentId/:skillId',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/notebook/collect/manual-entry/manual-entry-measurements-select/manual-entry-measurements-select.page').then(m => m.ManualEntryMeasurementsSelectPage)
  },
  {
    path: 'notebook/home/goals/create/:studentId',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/notebook/goals/create-goal/create-goal.page').then(m => m.CreateGoalPage)
  },
  {
    path: 'notebook/home/goals/create/',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/notebook/goals/create-goal/create-goal.page').then(m => m.CreateGoalPage)
  },
  {
    path: 'notebook',
    canActivate: [authGuard, emailVerifiedGuard, profileGuard],
    loadComponent: () => import('./pages/notebook/notebook.page').then(m => m.NotebookPage),
    children: [
      {
        path: 'home',
        loadComponent: () => import('./pages/notebook/home/home.page').then(m => m.HomePage)
      },
      {
        path: 'home/goals/:goalId',
        loadComponent: () => import('./pages/notebook/goals/goal-details/goal-details.page').then(m => m.GoalDetailsPage)
      },
      {
        path: 'caseload/groups/create',
        loadComponent: () => import('./pages/notebook/caseload/caseload-groups/caseload-group-create/caseload-group-create.page').then(m => m.CaseloadGroupCreatePage)
      },
      {
        path: 'caseload/groups/:groupId',
        loadComponent: () => import('./pages/notebook/caseload/caseload-groups/caseload-group-details/caseload-group-details.page').then(m => m.CaseloadGroupDetailsPage)
      },
      {
        path: 'caseload/:studentId',
        loadComponent: () => import('./pages/notebook/students/student-details/student-details.page').then(m => m.StudentDetailsPage)
      },
      {
        path: 'caseload',
        loadComponent: () => import('./pages/notebook/caseload/caseload-list/caseload-list.page').then(m => m.CaseloadListPage)
      },
      {
        path: 'goals/:goalId/edit',
        canActivate: [authGuard, emailVerifiedGuard, profileGuard],
        loadComponent: () => import('./pages/notebook/goals/goal-edit/goal-edit.page').then(m => m.GoalEditPage)
      },
      {
        path: 'goals/:goalId',
        loadComponent: () => import('./pages/notebook/goals/goal-details/goal-details.page').then(m => m.GoalDetailsPage)
      },
      {
        path: 'goals',
        loadComponent: () => import('./pages/notebook/goals/goals-list/goals-list.page').then(m => m.GoalsListPage)
      },
      {
        path: 'observations/:observationId',
        loadComponent: () => import('./pages/notebook/observations/observations-edit/observations-edit.page').then(m => m.ObservationsEditPage)
      },
      {
        path: 'observations',
        loadComponent: () => import('./pages/notebook/observations/observations-list/observations-list.page').then(m => m.ObservationsListPage)
      },
      {
        path: 'measurements',
        loadComponent: () => import('./pages/notebook/measurements/measurements-list/measurements-list.page').then(m => m.MeasurementsListPage)
      },
      {
        path: 'students/:studentId',
        loadComponent: () => import('./pages/notebook/students/student-details/student-details.page').then(m => m.StudentDetailsPage)
      },
      {
        path: 'collect/ai-companion',
        loadComponent: () =>
          import('./pages/notebook/collect/ai-companion/ai-companion.page').then(
            (m) => m.AiCompanionPage
          ),
      },
      {
        path: 'academy',
        loadComponent: () =>
          import('./pages-old/workspace/academy/academy.page').then(
            (m) => m.AcademyPage
          ),
      },
      {
        path: '**',
        redirectTo: 'home',
      }
    ],
  },
  {
    path: '**',
    redirectTo: 'home',
  },
  {
    path: 'admin-home',
    loadComponent: () => import('./pages/admin/admin-home/admin-home.page').then(m => m.AdminHomePage)
  },
  {
    path: 'admin-goals',
    loadComponent: () => import('./pages/admin/admin-goals/admin-goals.page').then(m => m.AdminGoalsPage)
  },
  {
    path: 'admin-observations',
    loadComponent: () => import('./pages/admin/admin-observations/admin-observations.page').then(m => m.AdminObservationsPage)
  },
];
